/**
 * The game session for one loaded level: world, view, audio, branding, and the ride in progress (drill, time
 * trial, free ride, enduro route, or a replay of the last run). The UI talks to it through src/app/main.ts.
 */
import { CLASS_TABLE, classifyDevice, compositionFor, coreMemoryMb, deviceKey, type DeviceClass, type DeviceFacts } from '../view/logic/deviceClass';
import { type ModuleId } from '../view/logic/registry';
import { FleetModule } from '../view/world/modules/fleet';
import { AudioModule } from '../view/world/modules/audio';
import { loadWorld, storage } from './worldStore';
import * as THREE from 'three/webgpu';
import { Coach } from '../game/coach';
import { DRILL_CONFIG, PROGRESSION_CONFIG } from '../game/config';
import { Drill, DrillEvent } from '../game/drill';
import { createDrill } from '../game/drills';
import { Figure8Drill } from '../game/drills/figure8';
import { LocalProgressStorage } from '../game/storage';
import { newlyUnlocked, Progress, recordDrill, recordEnduro, recordLap, recordTimeTrial } from '../game/progress';
import { SafeTrail } from '../game/safepoint';
import { CheckpointTimer, TimingEvent } from '../game/timing';
import { DT, GameWorld } from '../game/world';
import type { FrameState } from '../game/frame';
import { quatIdentity, rotate, vec } from '../sim/math';
import { blendPose } from '../sim/interp';
import type { BikeState } from '../sim/bike';
import { MessageKey } from '../i18n';
import { DEFAULT_BOT, jumpSpeedLimit, PathBot } from '../sim/bot';
import { BikeConfig } from '../sim/config';
import { DEFAULT_ENGINE } from '../audio/model';
import { FLEET, bikeSpec, fleetConfig } from '../sim/bikes';
import { decodeInput, encodeInput, InputFrame, INPUT_STRIDE_V2, NEUTRAL_INPUT } from '../sim/input';
import { Level, Placement } from '../sim/level';
import { ChunkedTerrain, GroundSave } from '../sim/chunks';
import { LEVELS, LevelId } from '../sim/levels';
import { loadLevelData } from '../sim/levels/load';
import { ChunkPrefetcher, generateInWorkers } from '../sim/prefetch';
import { runStep, type StepRunner } from '../ui/logic/loadPlan';
import { FixedStepper } from '../sim/stepper';
import type { PrettyView } from '../view/prettyView';
import { formatTime } from '../ui/logic/format';
import { hudNumbers } from '../ui/logic/hud';
import { TIME_STARS } from '../ui/logic/levels';
import { orbitAt } from '../ui/logic/orbit';
import { bikeLivery, helmetLivery, kitLivery } from '../ui/logic/livery';
import type { Settings } from '../ui/logic/settings';
import type { HudFrame } from '../ui/hud';
import type { CameraMode, Ride, RideResult } from '../ui/types';
import { GameAudio } from './audio';
import { DemoPlayer } from './howto';
import { DemoClip } from '../game/howto/demo';
import { ClipFile, clipFileName, ControlMode, decodeClip } from '../game/howto/clipfile';
import { configHash } from '../game/replay';
import type { TraceSample } from '../game/cinematic';
import { ClipOfferGate, Highlight, HighlightDetector } from '../game/highlights';
import type { WorldSnapshot } from '../game/world';
import { Situation } from '../game/howto/situation';
import { FailureWatcher } from '../game/howto/watcher';
import { addBranding } from './brand';
import { LiveryPainter } from './livery';

const TRACE_EVERY = 8; // steps between trace samples (30 per second at 240 Hz)
const FALL_RESET = 1.5; // s lying down before the rider is put back (track, enduro)
/** s added to the lap for a fall: the lap goes on from the last safe spot, it is not restarted. */
export const FALL_PENALTY = 5;

export interface SessionEvents {
  coach(key: MessageKey, params?: Record<string, string | number>): void;
  toast(key: MessageKey, params: Record<string, string | number>, kind: 'info' | 'good' | 'bad'): void;
  finish(result: RideResult): void;
  replayEnded(): void;
  /** A highlight worth a clip, at a calm moment (src/game/highlights.ts ClipOfferGate). */
  clipOffer?(h: Highlight): void;
  /** Something went wrong that a how-to demo can show (src/game/howto). */
  howto?(s: Situation): void;
  /** The how-to demo reached its end. */
  demoEnded?(): void;
}

interface Respawn { step: number; at: Placement }

/**
 * The GPU's name for the tier pick: WebGPU adapter info (vendor 'intel', architecture 'gen-12lp', ...) or the
 * WebGL renderer string. It was passed as '' and every desktop WebGPU adapter, the Iris Xe included, got high
 * (8 fps on the Iris Xe).
 */
function gpuName(renderer: THREE.WebGPURenderer): string {
  const b = renderer.backend as { device?: { adapterInfo?: { vendor?: string; architecture?: string; description?: string } }; gl?: WebGL2RenderingContext };
  const i = b.device?.adapterInfo;
  if (i) return `${i.vendor ?? ''} ${i.architecture ?? ''} ${i.description ?? ''}`.trim();
  const gl = b.gl, ext = gl?.getExtension('WEBGL_debug_renderer_info');
  return gl && ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) : '';
}

export class GameSession {
  readonly world: GameWorld;
  readonly progressStore = new LocalProgressStorage();
  readonly audio: GameAudio;
  readonly livery: LiveryPainter;
  mode: 'menu' | 'ride' | 'replay' = 'menu';
  paused = false;
  ride: Ride | null = null;
  camera: CameraMode = 'menu';
  events: SessionEvents | null = null;

  // (Up to 0.1 s of physics per frame: at 8 steps every frame over 33 ms on an iGPU dropped time and the ride hitched.)
  private readonly stepper = new FixedStepper(DT, 24);
  /** The bike's pose before the last physics step and the blended pose the view draws (blendPose). */
  private readonly prevPos = vec(); private readonly prevRot = quatIdentity();
  private readonly drawnPos = vec(); private readonly drawnRot = quatIdentity();
  private readonly drawnBike = {} as BikeState;
  private keepPrev(): void {
    const b = this.world.bike;
    this.prevPos.x = b.position.x; this.prevPos.y = b.position.y; this.prevPos.z = b.position.z;
    this.prevRot.x = b.orientation.x; this.prevRot.y = b.orientation.y; this.prevRot.z = b.orientation.z; this.prevRot.w = b.orientation.w;
  }
  private readonly prefetch: ChunkPrefetcher;
  private drill: Drill | null = null;
  private coach = new Coach();
  private timer: CheckpointTimer | null = null;
  private rideStart = 0;
  /** World step count at the ride's start: a replay runs on the same step numbers (the assists plan every 12th step). */
  private rideStartSteps = 0;
  /** The ground (ruts, knocked cones) as the ride found it: the replay rides that ground, not the one the ride left. */
  private groundAtStart: GroundSave | null = null;
  private lapStart = 0;
  private fallenFor = 0;
  /** Where a fall puts the rider back (the last safe spot, a little before the crash). */
  private trail = new SafeTrail(null);
  private lastInput: InputFrame = NEUTRAL_INPUT;
  private snap = true;
  private elapsed = 0;
  private orbitT = 0;
  private startPlacement: Placement;
  private inputs: number[] = [];
  private respawns: Respawn[] = [];
  private recordedStart: Placement | null = null;
  private lastSpawn: Placement | null = null;
  private atStart: Progress = this.progressStore.load();
  private replayIndex = 0;
  private replaySteps = 0;
  /** The ride's trace for the cinematic director (src/game/cinematic.ts): a sample every TRACE_EVERY steps. */
  trace: TraceSample[] = [];
  /** Replay speed (the cinematic's slow motion); 1 = real time. */
  replayScale = 1;
  /** The view a replay is shown from: the cinematic switches to the helmet camera for a segment. */
  replayView: 'chase' | 'fpv' = 'chase';
  /** The ride's highlights (big jumps, best laps, ...): the toast offers a clip, the director centres on the top one. */
  highlights: Highlight[] = [];
  private detector = new HighlightDetector();
  private clipGate = new ClipOfferGate();
  private loops = 0;
  /** A ride in progress put aside while its replay is filmed: stopReplay puts it back exactly. */
  private resume: { world: WorldSnapshot; lastInput: InputFrame; paused: boolean; camera: CameraMode; fallenFor: number } | null = null;
  private bot: PathBot | null = null;
  private watcher: FailureWatcher | null = null;
  /** The how-to demo playing, if any (it steps instead of the ride; the ride is put back when it ends). */
  demo: DemoPlayer | null = null;
  private readonly lookAt = new THREE.Vector3();

  private constructor(readonly levelId: LevelId, readonly view: PrettyView, world: GameWorld, public bikeId: string) {
    this.world = world;
    this.startPlacement = world.level.starts.default!;
    this.prefetch = new ChunkPrefetcher(world.terrain, levelId);
    this.audio = new GameAudio(levelId, bikeSpec(bikeId), world.cfg);
    this.livery = new LiveryPainter(view);
    view.onBikeLoaded = (root) => this.livery.attach(root);
    view.cameraOverride = (cam, dt) => this.menuCamera(cam, dt);
  }

  static async create(renderer: THREE.WebGPURenderer, levelId: LevelId, quality: Settings['quality'], bikeId: string, step: StepRunner = runStep): Promise<GameSession> {
    await step('level', () => loadLevelData(levelId), { retries: 1 });
    // The fleet bike (src/sim/bikes) is fixed for the page: a different one is a reload (see main.ts startRide).
    bikeId = bikeSpec(bikeId).id;
    const cfg: BikeConfig = fleetConfig(bikeId);
    // The start area's terrain chunks generate on workers (the spawn would otherwise generate them on the main thread).
    const world = await step('world', async () => {
      const level = new Level(LEVELS[levelId]), at = level.starts.default;
      const chunks = at ? await generateInWorkers(levelId, new ChunkedTerrain(level).missing(at.x, at.z, 160)).catch((err) => {
        console.warn('[load] chunk workers failed, generating inline', err);
        return [];
      }) : [];
      return new GameWorld(levelId, 'default', cfg, undefined, chunks);
    });
    const { PrettyView } = await step('modules', () => import('../view/prettyView'), { retries: 1 });
    const mobile = /Android|iPhone|iPad/i.test(navigator.userAgent) || matchMedia('(pointer: coarse)').matches;
    // The device class sets the core and the memory budget; the world is the saved fit for this device (Auto), else
    // composed under the budget from the class's start cap or the Settings choice. ?quality=full|medium|light overrides.
    const facts: DeviceFacts = {
      renderer: gpuName(renderer), webgpu: !!(renderer.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend, mobile,
      deviceMemory: (navigator as { deviceMemory?: number }).deviceMemory, screenPixels: screen.width * screen.height, shortSide: Math.min(screen.width, screen.height), touchPoints: navigator.maxTouchPoints,
    };
    const className = classifyDevice(facts), dc = CLASS_TABLE[className];
    const key = deviceKey(facts.renderer, screen.width, screen.height, devicePixelRatio);
    const urlQ = new URLSearchParams(location.search).get('quality');
    if (urlQ === 'full' || urlQ === 'medium' || urlQ === 'light') quality = urlQ;
    const stored = quality === 'auto' ? loadWorld(storage(), key) : null;
    const coreMb = coreMemoryMb(dc.mipSkip, dc.core.hd);
    const composition = stored?.composition ?? compositionFor(quality, dc, coreMb);
    const view = await PrettyView.create(renderer, world.level, world.terrain, cfg, { levelId, deviceClass: className, composition, bikeModel: bikeSpec(bikeId).model.file, step, gpu: facts.renderer });
    const s = new GameSession(levelId, view, world, bikeId);
    s.fit = { key, dc, auto: quality === 'auto', final: stored?.final ?? quality !== 'auto', coreMb };
    // Fleet and audio are world modules the session owns (they need it); main.ts builds them in its 'fleet' step.
    const brand = await step('branding', () => addBranding(view.scene, world.level, (x, z) => world.terrain.heightAt(x, z), s.startPlacement));
    const brandPaddock = (): void => { view.paddock?.setLivery(brand.art.van); view.paddock?.setFlag(brand.art.flag); };
    brandPaddock();
    // A menu fit that rebuilds the paddock re-applies its branding and livery.
    view.onModuleRebuilt = (id: ModuleId) => { if (id === 'paddock') { brandPaddock(); s.livery.attach(view.scene); } };
    s.livery.attach(view.scene);
    await step('warmup', () => view.warmUp(world.bike, NEUTRAL_INPUT), { optional: true });
    return s;
  }

  progress(): Progress { return this.progressStore.load(); }

  /**
   * The world fit for this device (main.ts drives it, src/view/logic/worldFit.ts): its storage key, class, whether it is
   * Auto (a fixed Settings choice is never fitted nor stepped down), whether the
   * fit is done (`final`: saved as final, or a fixed Settings choice), and the memory budget it composes under.
   */
  fit!: { key: string; dc: DeviceClass; auto: boolean; final: boolean; coreMb: number };

  /** Builds the session-owned world modules (fleet, audio) at the composition's levels. */
  async buildSessionModules(): Promise<void> {
    await this.view.world.attach(new FleetModule(() => this.preloadFleet(true), () => this.dropFleetModels()));
    await this.view.world.attach(new AudioModule((set) => this.audio.setLoopSet(set)));
  }

  applySettings(s: Settings): void {
    this.settings = s;
    this.audio.apply(s);
    this.livery.set(bikeLivery(s.bike), kitLivery(s.kit), helmetLivery(s.helmet));
  }

  private settings: Settings | null = null;
  private shownBike = '';
  private bikeSwap: Promise<void> = Promise.resolve();

  /**
   * Shows (garage, menu) and rides another fleet bike without a reload: model, physics config and engine sound.
   * Models are cached and shader-compiled off-screen (preloadFleet), so a swap is a scene-graph change.
   */
  setBike(id: string): Promise<void> {
    id = bikeSpec(id).id;
    if (id === (this.shownBike || this.bikeId)) return this.bikeSwap;
    this.shownBike = id;
    const spec = bikeSpec(id), cfg = fleetConfig(id);
    // Latest pick wins: a slower load that resolves after a later pick is dropped, never shown.
    this.bikeSwap = this.view.prepareBike(spec.model.file, cfg).then((g) => {
      if (this.shownBike !== id) return;
      const root = this.view.showBike(g, cfg);
      this.bikeId = id;
      this.world.cfg = cfg;
      this.audio.setBike(spec, cfg);
      this.livery.attach(root);
      if (this.settings) this.applySettings(this.settings);
    }).catch((err) => {
      console.warn('bike swap failed', id, err);
      if (this.shownBike === id) this.shownBike = this.bikeId; // the shown bike stays; picking this one again retries
    });
    return this.bikeSwap;
  }

  /** Whether a picked bike is still loading (the garage shows a spinner meanwhile). */
  get bikeLoading(): boolean { return this.shownBike !== '' && this.shownBike !== this.bikeId; }

  /**
   * `all` (the `fleet` world module at `all`): loads and prepares every fleet model, one at a time, so garage picks
   * swap at once. Otherwise only the shown bike is kept (the garage loads others on demand).
   */
  async preloadFleet(all: boolean): Promise<void> {
    if (!all) return;
    for (const b of FLEET) await this.view.prepareBike(b.model.file, fleetConfig(b.id)).catch(() => undefined);
  }

  /** Drops every prepared fleet model except the shown bike's (the `fleet` world module going to `current`). */
  dropFleetModels(): void {
    this.view.dropBikeModels(bikeSpec(this.bikeId).model.file);
  }

  setCamera(mode: CameraMode): void {
    this.camera = mode;
    this.view.cameraOverride = mode === 'ride' ? null : (cam, dt) => this.menuCamera(cam, dt);
    this.view.setMenuMode(mode !== 'ride'); // rim light + background depth of field (high) in the menu cameras
    if (mode === 'ride') this.snap = true;
  }

  // ---------- rides ----------

  start(ride: Ride): void {
    this.ride = ride;
    this.mode = 'ride';
    this.paused = false;
    this.audio.setPaused(false);
    const level = this.world.level;
    const at = ride.kind === 'drill' ? level.starts[ride.drill]! : ride.kind === 'oval' ? level.starts.oval! : level.starts.default!;
    // Free ride on the training oval (level 01): no timing, no how-to watcher; respawns and the autopilot follow the oval.
    const oval = ride.kind === 'oval' ? level.paths[0]!.line : null;
    this.spawn(at);
    this.world.terrain.releaseGround(this.groundAtStart);
    this.groundAtStart = this.world.terrain.saveGround();
    this.recordedStart = at;
    this.lastSpawn = null;
    this.atStart = this.progress();
    this.inputs = [];
    this.respawns = [];
    this.trace = [];
    this.highlights = [];
    this.detector = new HighlightDetector();
    this.clipGate = new ClipOfferGate();
    this.loops = 0;
    this.world.release(this.resume?.world);
    this.resume = null;
    this.drill = ride.kind === 'drill' ? createDrill(ride.drill) : null;
    this.coach = new Coach();
    this.watcher = ride.kind === 'drill' || oval ? null : new FailureWatcher(level);
    const laps = ride.kind === 'trial' ? PROGRESSION_CONFIG.timeTrialLaps : ride.kind === 'free' ? 999 : 1;
    this.timer = ride.kind === 'drill' || oval ? null : CheckpointTimer.forLevel(level, 'default', laps, this.world.time);
    this.rideStart = this.world.time;
    this.rideStartSteps = this.world.steps;
    this.lapStart = this.world.time;
    this.fallenFor = 0;
    this.trail = new SafeTrail(oval ?? level.line ?? null);
    this.trail.mark(at, this.world.time);
    const botLine = oval ?? level.line;
    this.bot = new URLSearchParams(location.search).get('autopilot') === '1' && botLine
      ? new PathBot(botLine, this.world.cfg, { ...DEFAULT_BOT, cruise: 11, speedLimit: oval ? undefined : jumpSpeedLimit(level) }, botLine.closest(at.x, at.z).s + 5) : null;
    this.setCamera('ride');
  }

  /** ?autopilot=1 rides in the player's control mode: in Simplified the assists run on its intent, as on a player's. */
  private autopilotInput(bot: PathBot): InputFrame {
    const i = bot.input(this.world.bike);
    return this.settings?.beginner ? { ...i, assist: true, autoFoot: true } : i;
  }

  quit(): void {
    this.mode = 'menu';
    this.ride = null;
    this.drill = null;
    this.timer = null;
    this.paused = false;
    this.audio.setPaused(false);
    this.spawn(this.startPlacement);
  }

  setPaused(p: boolean): void {
    this.paused = p;
    this.audio.setPaused(p);
  }

  private spawn(at: Placement): void {
    this.world.respawn(at);
    // Never a wheel below the ground after a respawn (tests/game/respawn.test.ts covers the lap; this catches the rest).
    if (!this.world.wheelsOnGround()) console.warn('respawn: a wheel is not standing on the ground', at);
    this.audio.resetHistory();
    this.snap = true;
    this.fallenFor = 0;
  }

  /** Respawn during a ride, remembered so the replay does the same at the same step. */
  private respawnInRide(at: Placement): void {
    this.spawn(at);
    this.watcher?.reset();
    if (this.mode === 'ride') this.respawns.push({ step: this.inputs.length / INPUT_STRIDE_V2, at });
  }

  /** This ride's best lap so far (s), null before the first lap or off the timed track. */
  get rideBestLap(): number | null { return this.timer?.bestLap ?? null; }

  /** Seconds of the ride recorded so far (the replay and the video cover this much). */
  get rideSeconds(): number { return (this.inputs.length / INPUT_STRIDE_V2) * DT; }

  /**
   * Plays the recorded ride from its start. During a ride (paused, filming a video) the ride is snapshotted first:
   * stopReplay puts the bike, the clock and the camera back where they were and the ride resumes from there.
   */
  startReplay(): boolean {
    if (!this.recordedStart || this.inputs.length === 0) return false;
    if (this.mode === 'ride') {
      this.resume = { world: this.world.snapshot(), lastInput: { ...this.lastInput }, paused: this.paused, camera: this.camera, fallenFor: this.fallenFor };
    }
    this.mode = 'replay';
    this.paused = false;
    if (this.groundAtStart) this.world.terrain.restoreGround(this.groundAtStart);
    this.spawn(this.recordedStart);
    this.world.steps = this.rideStartSteps;
    this.world.time = this.world.steps * this.world.dt;
    this.replayIndex = 0;
    this.replaySteps = this.inputs.length / INPUT_STRIDE_V2;
    this.replayScale = 1;
    this.replayView = 'chase';
    this.setCamera('ride');
    return true;
  }

  /** Ride time of the replay (s from the start of the recorded ride). */
  get replayTime(): number { return this.replayIndex * DT; }
  /** Whether the last replay step was played (the replay then ends on the next frame). */
  get replayDone(): boolean { return this.replayIndex >= this.replaySteps; }

  /** Steps the replay ahead to ride time t without rendering (the cinematic starts at its best stretch). */
  replaySkipTo(t: number): void {
    if (this.mode !== 'replay') return;
    const to = Math.min(this.replaySteps - 1, Math.round(t / DT));
    while (this.replayIndex < to) this.stepReplay();
    this.audio.resetHistory();
    this.snap = true;
  }

  stopReplay(): void {
    if (this.mode !== 'replay') return;
    const r = this.resume;
    if (r) {
      this.resume = null;
      this.mode = 'ride';
      this.world.restore(r.world);
      this.world.release(r.world);
      this.lastInput = r.lastInput;
      this.fallenFor = r.fallenFor;
      this.replayScale = 1;
      this.replayView = 'chase';
      this.audio.resetHistory();
      this.setPaused(r.paused);
      this.setCamera(r.camera);
      this.snap = true;
      return;
    }
    this.mode = 'menu';
    this.spawn(this.startPlacement);
    this.setCamera('menu');
  }

  // ---------- per frame ----------

  /** One rendered frame. `read` gives the live input for a physics step. */
  frame(dt: number, read: (dt: number) => InputFrame, reset: boolean): HudFrame | null {
    dt = Math.min(dt, 0.1);
    this.elapsed += dt;
    const bike = this.world.bike;
    this.prefetch.update(bike.position.x + bike.velocity.x * 4, bike.position.z + bike.velocity.z * 4);
    let alpha = 1;
    if (this.demo) {
      const d = this.demo;
      alpha = this.stepper.advance(dt * d.speed, () => { this.keepPrev(); d.step(); });
      this.lastInput = d.lastInput;
      if (d.done) this.events?.demoEnded?.();
    } else if (this.mode === 'ride' && !this.paused) {
      if (reset) this.resetInRide();
      alpha = this.stepper.advance(dt, () => { this.keepPrev(); this.stepRide(this.bot ? this.autopilotInput(this.bot) : read(DT)); });
    } else if (this.mode === 'replay' && !this.paused) {
      alpha = this.stepper.advance(dt * this.replayScale, () => { this.keepPrev(); this.stepReplay(); });
    }
    const input = this.mode === 'menu' && !this.demo ? NEUTRAL_INPUT : this.lastInput;
    this.world.terrain.update(bike.position.x, bike.position.z);
    // The helmet camera is the rider's: replays and how-to demos keep the chase camera.
    this.view.rideView = this.mode === 'ride' && !this.demo ? this.settings?.camera ?? 'chase' : this.mode === 'replay' ? this.replayView : 'chase';
    const scale = this.demo?.speed ?? (this.mode === 'replay' ? this.replayScale : 1);
    // Drawn between the last two physics steps (src/sim/interp.ts), so the motion is even whatever the frame rate.
    const live = this.world.bike;
    blendPose(this.prevPos, this.prevRot, live.position, live.orientation, this.snap ? 1 : alpha, this.drawnPos, this.drawnRot);
    const drawn = Object.assign(this.drawnBike, live, { position: this.drawnPos, orientation: this.drawnRot });
    this.view.frame(drawn, input, this.paused && !this.demo ? 1 / 60 : dt * scale, this.snap);
    this.snap = false;
    if (!this.paused || this.demo) {
      const surface = this.world.terrain.sample(bike.position.x, bike.position.z).surface;
      this.audio.frame(bike, this.mode === 'menu' ? 0 : input.throttle, surface, dt * scale);
    }
    return this.mode === 'menu' ? null : this.hudFrame();
  }

  private record(i: InputFrame): void {
    encodeInput(i, this.inputs);
  }

  private stepRide(input: InputFrame): void {
    if (this.mode !== 'ride') return;
    this.lastInput = input;
    const prev = { x: this.world.bike.position.x, z: this.world.bike.position.z };
    this.record(input);
    let f: FrameState;
    try { f = this.world.step(input); } catch (e) {
      // A blown-up step (bike state diverged): back on the bike at the last safe spot, recorded so the replay does
      // the same at the same step, instead of the error every frame from then on.
      console.warn('ride step failed, respawning', e);
      this.respawnInRide(this.trail.pick(this.world.time) ?? this.lastCheckpoint());
      return;
    }
    this.traceStep(f.bike);
    this.highlightStep(f);
    const events: DrillEvent[] = this.drill ? this.drill.evaluate(f) : [];
    if (this.drill instanceof Figure8Drill && this.drill.lobes !== this.loops) {
      // Two lobes close one figure eight.
      this.loops = this.drill.lobes;
      if (this.loops % 2 === 0) this.addHighlight(this.detector.loop(this.rideSeconds));
    }
    const line = this.coach.update(f, events);
    if (line) this.events?.coach(line.key, line.params);
    const failure = this.watcher?.step(f);
    if (failure) this.events?.howto?.(failure);
    for (const e of events) if (e.type === 'finished') { this.finishDrill(); return; }
    if (this.timer) for (const e of this.timer.update(prev, { x: f.bike.position.x, z: f.bike.position.z }, f.time, f.dt)) this.onTiming(e);
    if (this.mode !== 'ride' || this.drill) return;
    this.trail.update(f.bike, f.time);
    this.fallenFor = f.bike.telemetry.fallen ? this.fallenFor + f.dt : 0;
    if (this.fallenFor >= FALL_RESET) this.afterFall(f.time);
  }

  private traceStep(b: FrameState['bike']): void {
    const n = this.inputs.length / INPUT_STRIDE_V2; // steps ridden, this one included
    if ((n - 1) % TRACE_EVERY !== 0) return;
    const fwd = rotate(b.orientation, vec(0, 0, 1));
    this.trace.push({ t: (n - 1) * DT, x: b.position.x, y: b.position.y, z: b.position.z, speed: b.telemetry.speed,
      air: b.telemetry.airborne && !b.telemetry.fallen, heading: Math.atan2(fwd.x, fwd.z) });
  }

  private highlightStep(f: FrameState): void {
    const t = f.bike.telemetry, now = this.rideSeconds;
    for (const h of this.detector.step({ t: now, y: f.bike.position.y, airborne: t.airborne, fallen: t.fallen, wheelie: t.wheelie, landing: t.landing })) this.addHighlight(h);
    const shown = this.clipGate.step(now, DT, !t.airborne && !t.fallen, now, this.settings?.clipTips ?? true);
    if (shown) this.events?.clipOffer?.(shown);
  }

  private addHighlight(h: Highlight | null): void {
    if (!h) return;
    this.highlights.push(h);
    this.clipGate.offer(h, this.rideSeconds);
  }

  private stepReplay(): void {
    if (this.mode !== 'replay') return;
    const i = this.replayIndex;
    if (i >= this.replaySteps) { this.stopReplay(); this.events?.replayEnded(); return; }
    for (const r of this.respawns) if (r.step === i) this.spawn(r.at);
    const input = decodeInput(this.inputs, i * INPUT_STRIDE_V2);
    this.lastInput = input;
    this.replayIndex++;
    try { this.world.step(input); } catch { /* the ride failed this step too; its recorded respawn follows */ }
  }

  /** After a fall on a track or a route: back on the bike at the last safe spot; the lap goes on with a time penalty. */
  private afterFall(time: number): void {
    const at = this.trail.pick(time) ?? this.lastCheckpoint();
    if (this.timer) {
      this.timer.penalize(FALL_PENALTY);
      this.lapStart -= FALL_PENALTY;
      this.events?.toast('timing.fallPenalty', { s: FALL_PENALTY }, 'bad');
    }
    this.respawnInRide(at);
    this.trail.mark(at, time);
  }

  private lastCheckpoint(): Placement { return this.lastSpawn ?? this.recordedStart ?? this.startPlacement; }

  private resetInRide(): void {
    if (!this.ride) return;
    if (this.ride.kind === 'drill') { this.start(this.ride); return; }
    this.respawnInRide(this.lastCheckpoint());
  }

  private onTiming(e: TimingEvent): void {
    const ev = this.events;
    if (e.type === 'checkpoint') {
      this.lastSpawn = this.world.level.checkpoints[e.index] ?? this.lastSpawn;
      const slow = this.watcher?.checkpoint(e.index, e.time);
      if (slow) ev?.howto?.(slow);
      if (e.index !== 0) ev?.toast('timing.checkpoint', { n: e.index }, 'info');
    }
    if (e.type === 'missed') { ev?.toast('timing.missedCheckpoint', {}, 'bad'); this.respawnInRide(e.respawn); }
    if (e.type === 'lap') {
      this.lapStart = e.total + this.rideStart;
      const before = this.progress();
      const best = this.ride?.level === 'track' ? before.track.bestLap : null;
      if (this.ride?.level === 'track') this.progressStore.save(recordLap(before, e.time));
      const isBest = best === null || e.time < best;
      // A personal best needs one to beat; the first lap ever is just a clean lap.
      this.addHighlight(this.detector.lap(this.rideSeconds, e.time, best !== null && e.time < best));
      if (this.ride?.kind !== 'enduro') ev?.toast('timing.lap', { lap: e.lap, time: formatTime(e.time) }, isBest ? 'good' : 'info');
    }
    if (e.type === 'finished') this.finishTimed(e.laps, e.total);
  }

  private finishDrill(): void {
    const d = this.drill!, r = d.result()!;
    const before = this.progress();
    const after = recordDrill(before, d.id, r.stars);
    this.progressStore.save(after);
    this.mode = 'menu';
    this.events?.finish({
      ride: this.ride!, stars: r.stars, failed: r.stars === 0, drill: r, newBest: r.stars > before.drills[d.id],
      unlocked: newlyUnlocked(before, after), replayable: this.inputs.length > 0,
    });
    const failure = FailureWatcher.drill(d.id, r);
    if (failure) this.events?.howto?.(failure);
  }

  private finishTimed(laps: number[], total: number): void {
    const ride = this.ride!;
    // Laps were saved one by one as they came; compare against the progress from before this run.
    const before = this.atStart;
    let after = this.progress(), stars = 0, previousBest: number | null = null;
    if (ride.kind === 'trial') {
      after = recordTimeTrial(before, laps);
      previousBest = before.track.bestLap;
      const best = Math.min(...laps);
      stars = best < TIME_STARS.track[1] ? 3 : best < TIME_STARS.track[0] ? 2 : 1;
    } else if (ride.kind === 'enduro') {
      previousBest = before.enduro.bestTime;
      after = recordEnduro(before, total);
      stars = total < TIME_STARS.enduro[1] ? 3 : total < TIME_STARS.enduro[0] ? 2 : 1;
    }
    this.progressStore.save(after);
    this.mode = 'menu';
    const mine = ride.kind === 'enduro' ? total : Math.min(...laps);
    this.events?.finish({
      ride, stars, failed: false, laps: ride.kind === 'enduro' ? undefined : laps, total, previousBest,
      newBest: previousBest === null || mine < previousBest, unlocked: newlyUnlocked(before, after), replayable: true,
    });
  }


  // ---------- how-to demos ----------

  private clips = new Map<string, Promise<DemoClip | null>>();

  /**
   * The expert demo for a situation on this bike in this control mode: a few KB pre-generated at build time
   * (scripts/howto-clips.mjs -> public/howto), fetched once and cached. Null when there is none, or when it was
   * recorded on other physics (its config hash differs: it would not replay true).
   */
  demoClip(s: Situation, mode: ControlMode): Promise<DemoClip | null> {
    if (s.level !== this.levelId) return Promise.resolve(null);
    const name = clipFileName(s, this.bikeId, mode);
    let p = this.clips.get(name);
    if (!p) {
      const hash = configHash(this.world.cfg, DT);
      p = fetch(`${import.meta.env.BASE_URL}howto/${name}`)
        .then((r) => (r.ok ? r.json() as Promise<ClipFile> : null))
        .then((f) => (f && f.configHash === hash ? decodeClip(f) : null))
        .catch(() => null);
      p.then((c) => { if (!c) this.clips.delete(name); }, () => undefined); // a miss may be retried later
      this.clips.set(name, p);
    }
    return p;
  }

  /** Fetches, while the player rides, the demos this ride can offer (the drill, or the Zielonka jumps). */
  prefetchDemos(mode: ControlMode): void {
    const ride = this.ride;
    if (!ride) return;
    if (ride.kind === 'drill') { void this.demoClip({ level: 'ring', drill: ride.drill, cause: 'fell' }, mode); return; }
    if (ride.kind === 'oval') return;
    for (const f of this.world.level.features) {
      if ('refSpeed' in f) void this.demoClip({ level: this.levelId, s: f.s, feature: `jump@${f.s}`, cause: 'cased' }, mode);
    }
  }

  /** Plays a demo in the main view; the ride (or the menu scene) is put aside until stopDemo. */
  playDemo(clip: DemoClip, slow = false): void {
    if (this.demo && this.demo.clip !== clip) this.stopDemo();
    this.demo ??= new DemoPlayer(clip, this.world, this.view);
    this.demo.slow = slow;
    this.demo.begin();
    this.audio.setPaused(false);
    this.audio.resetHistory();
    this.snap = true;
  }

  setDemoSlow(slow: boolean): void { if (this.demo) this.demo.slow = slow; }

  stopDemo(): void {
    const d = this.demo;
    if (!d) return;
    this.demo = null;
    d.end();
    this.audio.resetHistory();
    this.audio.setPaused(this.paused);
    this.snap = true;
    this.setCamera(this.camera);
  }

  /**
   * "Try again from here": a drill starts over; on a track the rider is put at the demo's start (a few seconds
   * before the spot) and the timing carries on from the next checkpoint.
   */
  practiceFrom(clip: DemoClip): void {
    this.stopDemo();
    const ride = this.ride;
    if (!ride) return;
    if (ride.kind === 'drill') { this.start(ride); return; }
    if (this.mode !== 'ride') return;
    this.respawnInRide(clip.at);
    const level = this.world.level, line = level.line;
    if (this.timer && line) {
      const s = line.closest(clip.at.x, clip.at.z).s;
      const i = level.checkpoints.findIndex((c) => c.s > s);
      this.timer.next = i < 0 ? 0 : i;
    }
  }

  // ---------- HUD ----------

  private hudFrame(): HudFrame {
    const b = this.world.bike, t = b.telemetry;
    // The rev bar is scaled for the default engine: map this bike's idle..limiter onto it.
    const c = this.world.cfg, e = this.audio.engine;
    const rpm = DEFAULT_ENGINE.idleRpm + ((e.rpm - c.idleRpm) / (c.limiterRpm - c.idleRpm)) * (DEFAULT_ENGINE.limiterRpm - DEFAULT_ENGINE.idleRpm);
    const n = hudNumbers(t, this.lastInput, rpm, e.gear + 1);
    const ride = this.ride;
    const now = this.world.time;
    if (this.mode === 'replay' || !ride) return { n, title: '', time: null, waiting: false };
    if (ride.kind === 'oval') return { n, title: '', time: now - this.rideStart, waiting: false };
    if (ride.kind === 'drill' && this.drill) {
      const d = this.drill;
      const running = d.status !== 'idle';
      const frame: HudFrame = { n, title: '', time: running ? now - (d as unknown as { startTime: number }).startTime : 0, limit: DRILL_CONFIG[ride.drill].timeLimit, waiting: !running };
      if (d instanceof Figure8Drill) {
        const lobes = DRILL_CONFIG.figure8.lobes;
        frame.progress01 = d.lobes / lobes;
      }
      return frame;
    }
    const timer = this.timer!;
    const cps = this.world.level.checkpoints.length;
    const passed = (timer.next - 1 + cps) % cps;
    const f: HudFrame = { n, title: '', time: now - this.lapStart, waiting: false, progress01: passed / cps };
    if (ride.kind !== 'enduro') {
      f.last = timer.laps.length ? timer.laps[timer.laps.length - 1]! : null;
      f.best = timer.bestLap ?? this.progress().track.bestLap;
    } else f.time = now - this.rideStart;
    return f;
  }

  /** The drill's key numbers the HUD labels itself (lap n of total, loops). */
  hudCounters(): { lap?: [number, number]; cp?: [number, number]; loops?: [number, number] } {
    const ride = this.ride;
    if (!ride || this.mode !== 'ride') return {};
    if (ride.kind === 'drill') return this.drill instanceof Figure8Drill ? { loops: [this.drill.lobes, DRILL_CONFIG.figure8.lobes] } : {};
    if (ride.kind === 'oval') return {};
    const timer = this.timer!;
    const cps = this.world.level.checkpoints.length;
    const cp: [number, number] = [(timer.next - 1 + cps) % cps, cps];
    if (ride.kind === 'trial') return { lap: [Math.min(timer.laps.length + 1, PROGRESSION_CONFIG.timeTrialLaps), PROGRESSION_CONFIG.timeTrialLaps], cp };
    if (ride.kind === 'free') return { lap: [timer.laps.length + 1, 0], cp };
    return { cp };
  }

  // ---------- menu cameras ----------

  private menuCamera(cam: THREE.PerspectiveCamera, dt: number): void {
    this.orbitT += dt;
    const b = this.world.bike.position;
    const garage = this.camera === 'garage';
    const o = garage
      ? { cx: b.x, cy: b.y, cz: b.z, radius: 4.1, height: 0.75, speed: 0.22, phase: 0.6 }
      : { cx: b.x, cy: b.y, cz: b.z, radius: 5.2, height: 1.3, speed: 0.07, phase: 2.2 };
    const p = orbitAt(o, this.orbitT);
    const ground = this.world.terrain.heightAt(p.x, p.z) + 0.4;
    cam.position.set(p.x, Math.max(p.y, ground), p.z);
    // Frame the bike beside the menus: on wide screens the start menu is on the left (bike right of centre) and the
    // garage panel on the right (bike left of centre); portrait keeps it centred.
    const wide = cam.aspect > 1.2;
    const side = !wide ? 0 : garage ? 0.55 : -1.25;
    const rx = -(b.z - cam.position.z), rz = b.x - cam.position.x; // horizontal right of the view direction
    const rl = Math.hypot(rx, rz) || 1;
    this.lookAt.set(b.x + (rx / rl) * side, b.y + (garage ? 0.55 : wide ? 0.5 : 0.1), b.z + (rz / rl) * side);
    cam.lookAt(this.lookAt);
    const fov = garage ? 42 : 48;
    if (cam.fov !== fov) { cam.fov = fov; cam.updateProjectionMatrix(); }
  }
}

